import { TExtendedDiffOperation, TDiffStatus } from "../pages/ProjectDetailsPage/model/types/ExtendedDiffOperation";
import { isTableRow, TDiffOperation } from "@/zapi-frontend/utils/diff-sentence/diff-sentence";
import { nanoid } from "nanoid";
import { markdownToHtmlSync } from "./markdown";

export function isTableHeaderSeparator(text: string) {
  return /^\s*\|[\s-:|]+\|?\s*$/.test(text) || text.includes("| ---") || text.includes("|---");
}

function isHtmlTable(text: string): boolean {
  const trimmed = text.trim();
  return /<table[\s>]/i.test(trimmed) && /<\/table>/i.test(trimmed);
}

function isHtmlTableStart(text: string): boolean {
  const trimmed = text.trim();
  return /<table[\s>]/i.test(trimmed) || /^\s*<table[\s>]/i.test(text);
}

function isHtmlTableEnd(text: string): boolean {
  return /<\/table>/i.test(text);
}

function isHtmlTableRow(text: string): boolean {
  return (
    /<tr[\s>]/i.test(text) ||
    /<\/tr>/i.test(text) ||
    /<th[\s>]/i.test(text) ||
    /<td[\s>]/i.test(text) ||
    /<\/th>/i.test(text) ||
    /<\/td>/i.test(text)
  );
}

function hasValidTableStructure(content: string) {
  if (isHtmlTable(content)) {
    return true;
  }

  const lines = content.split("\n").filter((line) => line.trim().length > 0);
  const linesWithPipes = lines.filter((line) => line.includes("|"));
  return linesWithPipes.length >= 2 || (linesWithPipes.length === 1 && isTableHeaderSeparator(content));
}

function isTableStart(text: string) {
  if (isHtmlTableStart(text)) {
    return true;
  }

  return text.includes("|") && (text.trim().startsWith("|") || text.trim().startsWith("\n|"));
}

function isTableEnd(text: string) {
  if (isHtmlTableEnd(text)) {
    return true;
  }

  const lines = text.split("\n");
  let lastPipeLine = -1;

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes("|")) {
      lastPipeLine = i;
    }
  }

  return (
    lastPipeLine >= 0 &&
    (lastPipeLine === lines.length - 1 || lines.slice(lastPipeLine + 1).every((line) => !line.includes("|")))
  );
}

function normalizeTableSeparatorRow(line: string, colCount: number): string {
  const parts: string[] = [];
  for (let i = 0; i < colCount; i++) {
    parts.push("---");
  }
  return `| ${parts.join(" | ")} |`;
}

function normalizeTableRow(line: string): { normalized: string; colCount: number } {
  const raw = line.trim();

  const inner = raw.replace(/^\|/, "").replace(/\|$/, "");

  const cells = inner.split(/(?<!\\)\|/).map((c) => {
    const cell = c.replace(/\\\|/g, "|").trim().replace(/\s+/g, " ");
    return cell;
  });

  const finalCells = cells.length === 0 ? [""] : cells;

  const normalized = `| ${finalCells.join(" | ")} |`;
  return { normalized, colCount: finalCells.length };
}

function normalizeHtmlTable(content: string): string {
  let normalized = content
    .replace(/\s+/g, " ") // Normalize whitespace
    .replace(/<table[^>]*>/gi, "<table>")
    .replace(/<\/table>/gi, "</table>")
    .replace(/<thead[^>]*>/gi, "<thead>")
    .replace(/<\/thead>/gi, "</thead>")
    .replace(/<tbody[^>]*>/gi, "<tbody>")
    .replace(/<\/tbody>/gi, "</tbody>")
    .replace(/<tr[^>]*>/gi, "<tr>")
    .replace(/<\/tr>/gi, "</tr>")
    .replace(/<th[^>]*>/gi, "<th>")
    .replace(/<\/th>/gi, "</th>")
    .replace(/<td[^>]*>/gi, "<td>")
    .replace(/<\/td>/gi, "</td>")
    .trim();

  normalized = normalized.replace(/>\s+</g, "><");
  normalized = normalized.replace(/>\s+/g, ">");
  normalized = normalized.replace(/\s+</g, "<");

  return normalized;
}

export function normalizeTable(content: string): string {
  if (isHtmlTable(content)) {
    return normalizeHtmlTable(content);
  }

  const lines = content.split("\n");
  const out: string[] = [];
  let expectedCols: number | null = null;
  for (const line of lines) {
    if (line.trim() === "") {
      continue;
    }
    if (!line.includes("|")) {
      out.push(line.trim().replace(/\s+/g, " "));
      continue;
    }
    const isSep = isTableHeaderSeparator(line);
    if (isSep && expectedCols != null) {
      out.push(normalizeTableSeparatorRow(line, expectedCols));
      continue;
    }
    const { normalized, colCount } = normalizeTableRow(line);
    if (expectedCols == null) expectedCols = colCount;
    out.push(normalized);
  }
  return out.join("\n");
}

export const getProcessedDiffResults = (diffResult: TDiffOperation[]): TExtendedDiffOperation[] => {
  if (!diffResult || diffResult.length === 0) return [];

  const processedResult = [...diffResult] as TExtendedDiffOperation[];

  for (let i = 0; i < diffResult.length - 1; i++) {
    const op = diffResult[i];
    const prevOp = diffResult[i - 1];

    const trimmed = op.text.trim();

    const hasNoMeaningfulText = trimmed === "" || /^[\s\n\r]+$/.test(trimmed);

    if (hasNoMeaningfulText && op.type === prevOp.type) {
      processedResult[i - 1] = { ...op, text: prevOp.text + op.text };
      processedResult[i] = { ...processedResult[i], isSkip: true };
    }
  }

  const processed = [...processedResult] as TExtendedDiffOperation[];

  for (let i = 0; i < processed.length; i++) {
    const op = processed[i];
    if (op.isSkip) {
      continue;
    }

    if (isHtmlTable(op.text)) {
      processed[i] = {
        ...op,
        text: op.text,
        isCompleteTable: true
      };
      continue;
    }

    if (isHtmlTableStart(op.text) && i < processed.length - 1) {
      let tableContent = op.text;
      let endIndex = i;
      let hasFoundTableEnd = isHtmlTableEnd(op.text);

      if (!hasFoundTableEnd) {
        for (let j = i + 1; j < processed.length; j++) {
          tableContent += processed[j].text;
          endIndex = j;

          if (isHtmlTableEnd(processed[j].text)) {
            hasFoundTableEnd = true;
            break;
          }

          // If we encounter a different operation type or non-table content, stop
          if (processed[j].type !== processed[i].type && !isHtmlTableRow(processed[j].text)) {
            break;
          }
        }
      }

      if (hasFoundTableEnd && hasValidTableStructure(tableContent)) {
        processed[i] = {
          ...op,
          text: tableContent,
          isCompleteTable: true
        };
        for (let j = i + 1; j <= endIndex; j++) {
          processed[j] = { ...processed[j], text: "", isSkip: true };
        }
      }
      continue;
    }

    if (isTableStart(op.text) && i < processed.length - 1) {
      let tableContent = op.text;
      let endIndex = i;
      let hasFoundTableEnd = false;
      for (let j = i + 1; j < processed.length; j++) {
        if (!isTableRow(processed[j].text) || processed[j].type !== processed[i].type) {
          hasFoundTableEnd = true;
          endIndex = j - 1;
          break;
        }

        tableContent += processed[j].text;
        endIndex = j;

        if (isTableEnd(processed[j].text) && j < processed.length - 1 && !isTableRow(processed[j + 1].text)) {
          hasFoundTableEnd = true;
          break;
        }
      }

      if ((hasFoundTableEnd || endIndex === processed.length - 1) && endIndex > i) {
        if (hasValidTableStructure(tableContent)) {
          processed[i] = {
            ...op,
            text: tableContent,
            isCompleteTable: true
          };
          for (let j = i + 1; j <= endIndex; j++) {
            processed[j] = { ...processed[j], text: "", isSkip: true };
          }
        }
      }
    }
  }

  const noSkips0 = processed.filter((op) => !op.isSkip);

  for (let i = 0; i < noSkips0.length - 1; i++) {
    if (noSkips0[i].type === "delete" && noSkips0[i + 1].type === "insert") {
      const a = noSkips0[i].text;
      const b = noSkips0[i + 1].text;

      const aIsMarkdownTable = a.includes("|");
      const bIsMarkdownTable = b.includes("|");
      const aIsHtmlTable = isHtmlTable(a) || isHtmlTableStart(a);
      const bIsHtmlTable = isHtmlTable(b) || isHtmlTableStart(b);

      const bothLookLikeTables = (aIsMarkdownTable && bIsMarkdownTable) || (aIsHtmlTable && bIsHtmlTable);

      if (bothLookLikeTables && normalizeTable(a) === normalizeTable(b)) {
        noSkips0[i].type = "equal";
        noSkips0[i + 1].isSkip = true;
        continue;
      }
      if (a.trim() === b.trim()) {
        noSkips0[i].type = "equal";
        noSkips0[i + 1].isSkip = true;
      }
    }
  }

  const noSkips1 = noSkips0.filter((op) => !op.isSkip);

  const joined = noSkips1.reduce((acc, op) => {
    const last = acc[acc.length - 1];
    if (!last || last.type !== op.type || op.isCompleteTable || last.isCompleteTable) {
      acc.push(op);
    } else {
      acc[acc.length - 1].text += op.text;
    }
    return acc;
  }, [] as TExtendedDiffOperation[]);

  const final: TExtendedDiffOperation[] = joined
    .map((op) => ({
      ...op,
      id: nanoid(),
      status: op.type !== "equal" ? ("pending" as TDiffStatus) : ("accepted" as TDiffStatus)
    }))
    .filter((op) => op.text.trim().length > 0);

  return final;
};

const isMD = (text: string) => {
  return (
    text.includes("#") ||
    text.includes("*") ||
    text.includes("-") ||
    text.includes("+") ||
    text.includes("[") ||
    /\d+\./.test(text)
  );
};

const escapeHtml = (str: string) => str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const diffOperationsToHtml = (diffOperations: TExtendedDiffOperation[]) => {
  if (!diffOperations.length) return "";

  const parts: string[] = [];

  diffOperations.forEach((operation) => {
    const { text, type } = operation;

    const diffType = type === "insert" ? "add" : type === "delete" ? "del" : undefined;

    const markdownHtml = isMD(text) ? markdownToHtmlSync(text) : escapeHtml(text);

    if (type === "equal") {
      parts.push(markdownHtml);
      return;
    }

    const imageMatch = text.match(/!\[(.*?)\]\((.*?)\)/);
    const hasImage = !!imageMatch && imageMatch.length > 2;

    const startsWithBlockTag = /^(<\s*(h[1-6]|p|ul|ol|li|table|blockquote|pre|hr|div)[\s>])/i.test(markdownHtml.trim());

    const hasDoubleLineBreaks = text.includes("\n\n");

    const isBlock = operation.isCompleteTable || hasImage || startsWithBlockTag || hasDoubleLineBreaks;

    if (isBlock) {
      let html: string;
      if (hasImage) {
        const alt = imageMatch[1];
        const src = imageMatch[2];
        html = `<img src="${src}" alt="${escapeHtml(alt)}" class="my-2 rounded-lg"/>`;
      } else if (
        operation.isCompleteTable ||
        (text.includes("|") && (text.includes("\n|") || text.includes("| ---"))) ||
        isHtmlTable(text)
      ) {
        if (isHtmlTable(text)) {
          html = text;
        } else {
          html = markdownToHtmlSync(text);
        }
      } else {
        html = markdownHtml;
      }
      parts.push(`<div data-diff-type="${diffType}" class="diff-block">${html}</div>`);
    } else {
      const html = markdownHtml;
      if (diffType) {
        parts.push(`<span data-diff-type="${diffType}" class="diff-inline">${html}</span>`);
      } else {
        parts.push(html);
      }
    }
  });

  return parts.join("");
};

export const hasPendingDiffs = (htmlContent: string) => {
  return htmlContent.includes("data-diff-type");
};
